import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _regeneratorRuntime from "/opt/build/repo/node_modules/@babel/runtime/regenerator/index.js";
function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
import { __rest } from 'tslib';
import { invariant } from 'hey-listen';
import { noop } from '@motionone/utils';
import { animateStyle } from '../animate/animate-style.es.js';
import { style } from '../animate/style.es.js';
import { getOptions } from '../animate/utils/options.es.js';
import { hasChanged } from './utils/has-changed.es.js';
import { resolveVariant } from './utils/resolve-variant.es.js';
import { scheduleAnimation, unscheduleAnimation } from './utils/schedule.es.js';
import { inView } from './gestures/in-view.es.js';
import { hover } from './gestures/hover.es.js';
import { press } from './gestures/press.es.js';
import { motionEvent } from './utils/events.es.js';
var gestures = {
  inView: inView,
  hover: hover,
  press: press
};
/**
 * A list of state types, in priority order. If a value is defined in
 * a righter-most type, it will override any definition in a lefter-most.
 */
var stateTypes = ["initial", "animate"].concat(_toConsumableArray(Object.keys(gestures)), ["exit"]);
/**
 * A global store of all generated motion states. This can be used to lookup
 * a motion state for a given Element.
 */
var mountedStates = new WeakMap();
function createMotionState() {
  var _marked = /*#__PURE__*/_regeneratorRuntime.mark(animateUpdates);
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var parent = arguments.length > 1 ? arguments[1] : undefined;
  /**
   * The element represented by the motion state. This is an empty reference
   * when we create the state to support SSR and allow for later mounting
   * in view libraries.
   *
   * @ts-ignore
   */
  var element;
  /**
   * Calculate a depth that we can use to order motion states by tree depth.
   */
  var depth = parent ? parent.getDepth() + 1 : 0;
  /**
   * Track which states are currently active.
   */
  var activeStates = {
    initial: true,
    animate: true
  };
  /**
   * A map of functions that, when called, will remove event listeners for
   * a given gesture.
   */
  var gestureSubscriptions = {};
  /**
   * Initialise a context to share through motion states. This
   * will be populated by variant names (if any).
   */
  var context = {};
  var _iterator = _createForOfIteratorHelper(stateTypes),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var name = _step.value;
      context[name] = typeof options[name] === "string" ? options[name] : parent === null || parent === void 0 ? void 0 : parent.getContext()[name];
    }
    /**
     * If initial is set to false we use the animate prop as the initial
     * animation state.
     */
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  var initialVariantSource = options.initial === false ? "animate" : "initial";
  /**
   * Destructure an initial target out from the resolved initial variant.
   */
  var _a = resolveVariant(options[initialVariantSource] || context[initialVariantSource], options.variants) || {},
    target = __rest(_a, ["transition"]);
  /**
   * The base target is a cached map of values that we'll use to animate
   * back to if a value is removed from all active state types. This
   * is usually the initial value as read from the DOM, for instance if
   * it hasn't been defined in initial.
   */
  var baseTarget = Object.assign({}, target);
  /**
   * A generator that will be processed by the global animation scheduler.
   * This yeilds when it switches from reading the DOM to writing to it
   * to prevent layout thrashing.
   */
  function animateUpdates() {
    var _a, _b, prevTarget, animationOptions, _iterator2, _step2, name, variant, key, allTargetKeys, animationFactories, animations, animationTarget;
    return _regeneratorRuntime.wrap(function animateUpdates$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          prevTarget = target;
          target = {};
          animationOptions = {};
          _iterator2 = _createForOfIteratorHelper(stateTypes);
          _context.prev = 4;
          _iterator2.s();
        case 6:
          if ((_step2 = _iterator2.n()).done) {
            _context.next = 24;
            break;
          }
          name = _step2.value;
          if (activeStates[name]) {
            _context.next = 10;
            break;
          }
          return _context.abrupt("continue", 22);
        case 10:
          variant = resolveVariant(options[name]);
          if (variant) {
            _context.next = 13;
            break;
          }
          return _context.abrupt("continue", 22);
        case 13:
          _context.t0 = _regeneratorRuntime.keys(variant);
        case 14:
          if ((_context.t1 = _context.t0()).done) {
            _context.next = 22;
            break;
          }
          key = _context.t1.value;
          if (!(key === "transition")) {
            _context.next = 18;
            break;
          }
          return _context.abrupt("continue", 14);
        case 18:
          target[key] = variant[key];
          animationOptions[key] = getOptions((_b = (_a = variant.transition) !== null && _a !== void 0 ? _a : options.transition) !== null && _b !== void 0 ? _b : {}, key);
          _context.next = 14;
          break;
        case 22:
          _context.next = 6;
          break;
        case 24:
          _context.next = 29;
          break;
        case 26:
          _context.prev = 26;
          _context.t2 = _context["catch"](4);
          _iterator2.e(_context.t2);
        case 29:
          _context.prev = 29;
          _iterator2.f();
          return _context.finish(29);
        case 32:
          allTargetKeys = new Set([].concat(_toConsumableArray(Object.keys(target)), _toConsumableArray(Object.keys(prevTarget))));
          animationFactories = [];
          allTargetKeys.forEach(function (key) {
            var _a;
            if (target[key] === undefined) {
              target[key] = baseTarget[key];
            }
            if (hasChanged(prevTarget[key], target[key])) {
              (_a = baseTarget[key]) !== null && _a !== void 0 ? _a : baseTarget[key] = style.get(element, key);
              animationFactories.push(animateStyle(element, key, target[key], animationOptions[key]));
            }
          });
          // Wait for all animation states to read from the DOM
          _context.next = 37;
          return;
        case 37:
          animations = animationFactories.map(function (factory) {
            return factory();
          }).filter(Boolean);
          if (animations.length) {
            _context.next = 40;
            break;
          }
          return _context.abrupt("return");
        case 40:
          animationTarget = target;
          element.dispatchEvent(motionEvent("motionstart", animationTarget));
          Promise.all(animations.map(function (animation) {
            return animation.finished;
          })).then(function () {
            element.dispatchEvent(motionEvent("motioncomplete", animationTarget));
          }).catch(noop);
        case 43:
        case "end":
          return _context.stop();
      }
    }, _marked, null, [[4, 26, 29, 32]]);
  }
  var setGesture = function setGesture(name, isActive) {
    return function () {
      activeStates[name] = isActive;
      scheduleAnimation(state);
    };
  };
  var updateGestureSubscriptions = function updateGestureSubscriptions() {
    for (var name in gestures) {
      var isGestureActive = gestures[name].isActive(options);
      var remove = gestureSubscriptions[name];
      if (isGestureActive && !remove) {
        gestureSubscriptions[name] = gestures[name].subscribe(element, {
          enable: setGesture(name, true),
          disable: setGesture(name, false)
        }, options);
      } else if (!isGestureActive && remove) {
        remove();
        delete gestureSubscriptions[name];
      }
    }
  };
  var state = {
    update: function update(newOptions) {
      if (!element) return;
      options = newOptions;
      updateGestureSubscriptions();
      scheduleAnimation(state);
    },
    setActive: function setActive(name, isActive) {
      if (!element) return;
      activeStates[name] = isActive;
      scheduleAnimation(state);
    },
    animateUpdates: animateUpdates,
    getDepth: function getDepth() {
      return depth;
    },
    getTarget: function getTarget() {
      return target;
    },
    getOptions: function getOptions() {
      return options;
    },
    getContext: function getContext() {
      return context;
    },
    mount: function mount(newElement) {
      invariant(Boolean(newElement), "Animation state must be mounted with valid Element");
      element = newElement;
      mountedStates.set(element, state);
      updateGestureSubscriptions();
      return function () {
        mountedStates.delete(element);
        unscheduleAnimation(state);
        for (var key in gestureSubscriptions) {
          gestureSubscriptions[key]();
        }
      };
    },
    isMounted: function isMounted() {
      return Boolean(element);
    }
  };
  return state;
}
export { createMotionState, mountedStates };